import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SearchMatch } from '@assembly/slack'
import { useWorkspace } from '../data/workspace'
import { keys } from '../data/keys'
import { useUser } from '../data/hooks'
import { useUi } from '../state/ui'
import { formatDateTime } from '../format'
import { Icon } from './Icon'
import { Files, Mrkdwn } from './MessageContent'
import { IconButton, MessageAvatar, Spinner, authorName, useIsNarrow } from './common'

function Result({ match }: { match: SearchMatch }) {
  const user = useUser(match.user)
  const openChannel = useUi((s) => s.openChannel)
  const openPanel = useUi((s) => s.openPanel)
  const channel = match.channel as { id: string; name?: string; is_im?: boolean; is_mpim?: boolean; is_private?: boolean }
  const threadTs = (() => {
    try {
      return new URL(match.permalink).searchParams.get('thread_ts')
    } catch {
      return null
    }
  })()
  const open = () => {
    openChannel(channel.id, { highlight: threadTs ?? match.ts })
    if (threadTs) openPanel({ type: 'thread', channel: channel.id, ts: threadTs })
  }
  return (
    <button type="button" className="search-result" onClick={open}>
      <div className="search-result-where muted">
        <Icon name={channel.is_im || channel.is_mpim ? 'user' : channel.is_private ? 'lock' : 'hash'} size={13} />
        {channel.is_im ? 'Direct message' : channel.name}
        {threadTs && ' · in a thread'}
      </div>
      <div className="search-result-body">
        <MessageAvatar message={match} size={32} />
        <div>
          <div className="message-header">
            <strong>{authorName(match, user)}</strong>
            <time>{formatDateTime(match.ts)}</time>
          </div>
          <Mrkdwn text={match.text} />
          {match.files && <Files files={match.files} />}
        </div>
      </div>
    </button>
  )
}

export function SearchView({ initialQuery }: { initialQuery: string }) {
  const { account, client } = useWorkspace()
  const goHome = useUi((s) => s.goHome)
  const narrow = useIsNarrow()
  const [input, setInput] = useState(initialQuery)
  const [query, setQuery] = useState(initialQuery)
  const [page, setPage] = useState(1)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => ref.current?.focus(), [])
  useEffect(() => {
    setInput(initialQuery)
    setQuery(initialQuery)
  }, [initialQuery])

  const { data, isFetching, error } = useQuery({
    queryKey: [...keys.search(account.id, query), page],
    queryFn: () => client.search.messages(query, page),
    enabled: query.trim().length > 0,
    placeholderData: (prev) => prev,
  })

  return (
    <section className="search-view">
      <header className="channel-header">
        <div className="channel-header-main">
          {narrow && <IconButton icon="chevronLeft" label="Back" onClick={goHome} className="back-button" />}
          <form
            className="search-form"
            onSubmit={(e) => {
              e.preventDefault()
              setPage(1)
              setQuery(input.trim())
            }}
          >
            <Icon name="search" size={16} />
            <input ref={ref} value={input} placeholder="Search messages" onChange={(e) => setInput(e.target.value)} />
            {isFetching && <Spinner size={16} />}
          </form>
        </div>
      </header>
      <div className="search-results">
        {!query && (
          <div className="search-help muted">
            <p>Narrow results with modifiers:</p>
            <ul>
              <li>
                <code>in:#channel</code> messages in a channel
              </li>
              <li>
                <code>from:@name</code> messages from someone
              </li>
              <li>
                <code>before:2026-01-01</code> / <code>after:yesterday</code> / <code>on:today</code>
              </li>
              <li>
                <code>has:link</code> <code>has:pin</code> <code>has::eyes:</code>
              </li>
              <li>
                <code>"exact phrase"</code> and <code>-exclude</code>
              </li>
            </ul>
          </div>
        )}
        {error && <p className="pad">Search failed: {(error as Error).message}</p>}
        {data && query && (
          <>
            <p className="muted pad-x">
              {data.total.toLocaleString()} result{data.total === 1 ? '' : 's'}
            </p>
            {data.matches.map((m) => (
              <Result key={`${m.iid ?? ''}${m.ts}`} match={m} />
            ))}
            {data.paging.pages > 1 && (
              <div className="pager">
                <button type="button" className="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <span className="muted">
                  Page {data.paging.page} of {data.paging.pages}
                </span>
                <button type="button" className="button" disabled={page >= data.paging.pages} onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
